import { useId } from "react";
import { useFormContext } from "react-hook-form";

import { Dialog } from "ui/dialog";
import CampaignTemplateSelectionStep, {
  CampaignTemplateSelectionStepActions,
} from "components/modals/add-touchpoints-from-template-modal/steps/campaign-template-selection-step/campaign-template-selection-step";
import TouchpointSelectionStep, {
  TouchpointSelectionStepActions,
} from "components/modals/add-touchpoints-from-template-modal/steps/touchpoint-selection-step/touchpoint-selection-step";

import { useAddTouchpointsFromMondayTemplateContext } from "components/modals/add-touchpoints-from-template-modal/context/add-touchpoints-from-monday-template-context";

import { useAvailableCampaignTemplates } from "components/modals/add-touchpoints-from-template-modal/hooks/use-available-campaign-templates";
import { useCreateFoldersFromTemplate } from "components/modals/add-touchpoints-from-template-modal/hooks/use-create-folders-from-template";
import { useCreateTouchpointsInFolder } from "components/modals/add-touchpoints-from-template-modal/hooks/use-create-touchpoints-in-folder";
import { useUsedCampaignTemplateIds } from "components/modals/add-touchpoints-from-template-modal/hooks/use-used-campaign-template-ids";
import { useFolderCampaignTemplate } from "components/modals/add-touchpoints-from-template-modal/hooks/use-folder-campaign-template";

import type { AddTouchpointsFromMondayTemplateFormData } from "components/modals/add-touchpoints-from-template-modal/add-touchpoints-from-template-modal.types";

const AddTouchpointsFromTemplateModalContent = () => {
  const { step, total, setIsOpen, campaignObjective, isDirectToStep2, folderId } =
    useAddTouchpointsFromMondayTemplateContext();
  const formId = useId();
  const { watch, handleSubmit } = useFormContext<AddTouchpointsFromMondayTemplateFormData>();
  const { createFolders } = useCreateFoldersFromTemplate();
  const { createTouchpointsInFolder } = useCreateTouchpointsInFolder();
  const selectedStrategy = watch("strategy");
  const populationType = watch("populationType");

  useAvailableCampaignTemplates({
    selectedStrategy,
    campaignObjective,
    populationType,
  });

  useUsedCampaignTemplateIds();

  // When in direct-to-step-2 mode (folder context), fetch the campaign template for the folder
  useFolderCampaignTemplate();

  const onSubmit = async (formData: AddTouchpointsFromMondayTemplateFormData) => {
    // Conditional submit: if in folder mode, add to existing folder, otherwise create new folders
    if (isDirectToStep2 && folderId) {
      await createTouchpointsInFolder(formData);
    } else {
      await createFolders(formData);
    }
  };

  const renderStep = () => {
    return step === 2 ? <TouchpointSelectionStep /> : <CampaignTemplateSelectionStep />;
  };

  return (
    <Dialog
      open
      onClose={() => setIsOpen(false)}
      size="medium"
      headline="Create from strategy"
      step={isDirectToStep2 ? undefined : { current: step, total }}
      actions={
        step === 2 ? (
          <TouchpointSelectionStepActions formId={formId} />
        ) : (
          <CampaignTemplateSelectionStepActions />
        )
      }>
      <form id={formId} className="flex flex-1 flex-col" onSubmit={handleSubmit(onSubmit)}>
        {renderStep()}
      </form>
    </Dialog>
  );
};

export default AddTouchpointsFromTemplateModalContent;
